// V2 Pipeline board — BUSINESS flow (local-business pipeline). Board-split
// Phase 1: this page only renders leads where flow === 'business'.
//
//   Prospect → Engaged → Completed
//
// Cloned from the coach board and specialized: business lane taxonomy, no
// LinkedIn invite automation machinery (that's coach-flow only), no connected
// article-share queue. LeadCard rendering, magic-link pills, drag-drop and the
// add-prospect tile carry over unchanged — the channel-icon strip already
// supports business contacts (WhatsApp / SMS / Email / website, v2.20.0).
//
// Complete code isolation from AviaryV2BoardCoachPage.js is deliberate:
// changes to one board cannot break the other.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Avatar,
  Box,
  Center,
  Group,
  Indicator,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowRight,
  IconArrowUpRight,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandWhatsapp,
  IconCircleCheck,
  IconDeviceMobile,
  IconGripVertical,
  IconLink,
  IconMail,
  IconMessage,
  IconPlus,
  IconSearch,
  IconWorld,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import useApi from '../hooks/useApi';
import { DndContext, PointerSensor, closestCorners, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  addProspectByUrl,
  fetchCampaigns,
  fetchLeads,
  updateLead,
} from '../services/api';
import {
  buildMailtoUrl,
  buildSmsUrl,
  buildWhatsappUrl,
  normaliseAuPhone,
  pickBestMessage,
} from '../lib/messageLinks';

const BOARD_PATH = '/v2/board/businesses';

// ─── Lane taxonomy ───────────────────────────────────────────────────────────
const LANES = ['prospect', 'engaged', 'completed'];
const LANE_TITLES = {
  prospect: 'Prospect',
  engaged: 'Engaged',
  completed: 'Completed',
};

const TERMINAL_STAGES = new Set(['won', 'closed', 'booked']);
// Any stage an operator could set that means "we've started a conversation" —
// includes legacy coach-ish stages so manual DnD into Engaged works even
// before any outbound turn exists.
const ENGAGED_STAGES = new Set([
  'engaged', 'contacted', 'invited', 'sent', 'connected', 'clicked',
  'hot', 'warm', 'curious', 'nurturing', 'article_opened', 'replying', 'meeting',
]);

function laneOf(lead) {
  const stage = String(lead.stage || 'prospect').toLowerCase();
  const episodeClosed = Boolean(lead?.closedAt || lead?.closeReason);
  const outboundPairs = Array.isArray(lead?.engagement?.pairs) ? lead.engagement.pairs : [];
  if (episodeClosed || TERMINAL_STAGES.has(stage)) return 'completed';
  if (outboundPairs.length > 0 || ENGAGED_STAGES.has(stage)) return 'engaged';
  return 'prospect';
}

// Drag-target → patch payload.
function laneToPatch(targetLane) {
  const nowIso = new Date().toISOString();
  if (targetLane === 'prospect') return { stage: 'prospect' };
  if (targetLane === 'engaged') return { stage: 'engaged', lastTouchAt: nowIso, lastActivityAt: nowIso };
  if (targetLane === 'completed') return { stage: 'won', lastActivityAt: nowIso };
  return null;
}

function terminalStyle(lead) {
  const reason = String(lead?.closeReason || '').toLowerCase();
  if (reason === 'opted_out') {
    return { bg: 'var(--mantine-color-gray-6)', fg: '#fff', label: 'Opted Out', emoji: '❌' };
  }
  if (reason === 'declined') {
    return { bg: 'var(--mantine-color-yellow-6)', fg: '#fff', label: 'Declined', emoji: '' };
  }
  if (reason === 'timed_out') {
    return { bg: 'var(--mantine-color-gray-5)', fg: '#fff', label: 'Timed Out', emoji: '⏱' };
  }
  return { bg: 'var(--mantine-color-sage-7)', fg: '#fff', label: 'Won', emoji: '🎉' };
}

// ─── Small helpers ───────────────────────────────────────────────────────────
function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function fitColorFor(score) {
  const v = Number(score || 0);
  if (v >= 80) return 'var(--mantine-color-sage-6)';
  if (v >= 60) return 'var(--mantine-color-sage-4)';
  return 'var(--aviary-muted)';
}

function finiteSortOrder(value) {
  return (typeof value === 'number' && Number.isFinite(value)) ? value : null;
}

function computeProspectDropSortOrder(orderedProspects, movedId, currentSortOrder) {
  const index = orderedProspects.findIndex((lead) => String(lead.id) === String(movedId));
  if (index < 0) return currentSortOrder;
  const prev = index > 0 ? orderedProspects[index - 1] : null;
  const next = index < (orderedProspects.length - 1) ? orderedProspects[index + 1] : null;
  const prevOrder = finiteSortOrder(prev?.sortOrder);
  const nextOrder = finiteSortOrder(next?.sortOrder);

  if (!prev && !next) return currentSortOrder;
  if (!prev) {
    if (nextOrder != null) return nextOrder - 1000;
    const current = finiteSortOrder(currentSortOrder);
    return (current != null ? current : 0) - 1000;
  }
  if (!next) {
    if (prevOrder != null) return prevOrder + 1000;
    const current = finiteSortOrder(currentSortOrder);
    return (current != null ? current : 1000) + 1000;
  }
  if (prevOrder != null && nextOrder != null) return (prevOrder + nextOrder) / 2;
  if (prevOrder != null) return prevOrder + 1000;
  if (nextOrder != null) return nextOrder - 1000;
  return currentSortOrder;
}

function formatAge(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(diff) || diff < 0) return '';
  const days = Math.floor(diff / 86400000);
  const hrs  = Math.floor(diff / 3600000);
  const mins = Math.floor(diff / 60000);
  if (days > 0) return `${days}d`;
  if (hrs > 0) return `${hrs}h`;
  if (mins > 0) return `${mins}m`;
  return 'now';
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

function normalizeUrl(raw, { hostPrefix = '' } = {}) {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('//')) return `https:${value}`;
  if (hostPrefix) {
    if (value.startsWith('@')) return `${hostPrefix}/${value.slice(1)}`;
    if (!value.includes('.') && !value.includes('/')) return `${hostPrefix}/${value}`;
  }
  return `https://${value.replace(/^\/+/, '')}`;
}

function FitChip({ score }) {
  const v = Math.round(Number(score || 0));
  if (!v || v < 10) return null;
  return (
    <Box style={{
      width: 32, height: 22, borderRadius: 12,
      border: `1.5px solid ${fitColorFor(v)}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Text size="xs" fw={600} style={{ color: 'var(--aviary-ink)', lineHeight: 1 }}>{v}</Text>
    </Box>
  );
}

function fallbackLabelForSource(source) {
  if (source === 'campaign-touch') return 'Touch';
  return 'Article';
}

function MagicLinkPill({ item }) {
  const read = Boolean(item?.readAt);
  const clicked = Boolean(item?.clickedAt);
  const sent = Boolean(item?.sentAt);
  const forwards = Number(item?.forwardedReaderCount || 0);
  const textColor = read
    ? 'var(--aviary-ink)'
    : (clicked ? 'var(--mantine-color-yellow-8)' : 'var(--aviary-muted)');
  const iconColor = read
    ? 'var(--mantine-color-sage-7)'
    : (clicked ? 'var(--mantine-color-yellow-7)' : 'var(--aviary-muted)');
  const rawLabel = String(item?.destinationLabel || '').trim();
  const fallbackLabel = fallbackLabelForSource(String(item?.source || '').trim());
  const label = rawLabel || fallbackLabel;
  const parts = [];
  if (sent) parts.push(`sent ${formatAge(item.sentAt)} ago`);
  if (clicked) parts.push(`clicked ${formatAge(item.clickedAt)} ago`);
  if (read) parts.push(`read ${formatAge(item.readAt)} ago`);
  if (forwards > 0) parts.push(`${forwards} forwards`);
  const tooltip = parts.length > 0 ? `${label} — ${parts.join(' · ')}` : label;
  const href = String(item?.destinationUrl || '').trim();
  const chip = (
    <UnstyledButton
      component="a"
      href={href || undefined}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'block' }}
    >
      <Paper
        radius="sm"
        p={4}
        withBorder
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          maxWidth: 260,
          background: '#fff',
          borderColor: 'var(--aviary-disabled)',
        }}
      >
        <IconLink size={12} style={{ color: iconColor, flexShrink: 0 }} />
        <Text
          size="xs"
          truncate
          style={{
            color: textColor,
            lineHeight: 1.2,
            flex: 1,
            minWidth: 0,
            fontStyle: rawLabel ? 'normal' : 'italic',
          }}
        >
          {label}
        </Text>
        {read && (
          <IconCircleCheck size={12} style={{ color: 'var(--mantine-color-sage-7)', flexShrink: 0 }} />
        )}
      </Paper>
    </UnstyledButton>
  );

  return (
    <Tooltip label={tooltip} withArrow>
      <Box style={{ display: 'inline-flex' }}>
        <Indicator
          size={7}
          color="orange"
          offset={2}
          position="top-end"
          withBorder
          disabled={!(forwards > 0)}
        >
          {chip}
        </Indicator>
      </Box>
    </Tooltip>
  );
}

function EngagementDots({ counts }) {
  const pairs = Array.isArray(counts?.pairs) ? counts.pairs : [];
  if (!pairs.length) return null;
  return (
    <Group gap={6} wrap="nowrap">
      {pairs.map((pair, index) => (
        <Box
          key={`${pair?.outboundAt || 'out'}-${index}`}
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            border: pair?.replied === true
              ? '1px solid var(--mantine-color-sage-6)'
              : '1px solid var(--aviary-muted)',
            background: pair?.replied === true ? 'var(--mantine-color-sage-6)' : 'transparent',
            flexShrink: 0,
          }}
        />
      ))}
    </Group>
  );
}

// ─── LeadCard — three-line tile ──────────────────────────────────────────────
function LeadCard({
  lead, lane, onClick, onDragStart, onDragEnd, isDragging,
  nativeDraggable = true,
  dndRef = null,
  prospectHandle = null,
  dndStyle = null,
  dndIsDragging = false,
}) {
  const isTerminal = lane === 'completed';
  const terminal = terminalStyle(lead);
  const [isHovering, setIsHovering] = useState(false);
  const iconColor = isTerminal ? 'rgba(255,255,255,0.85)' : 'var(--aviary-muted)';

  const channelLinks = useMemo(() => {
    const channels = [];
    const message = pickBestMessage(lead);
    const email = firstNonEmpty(lead.email, lead.contactEnrichment?.emails?.[0]);
    if (email) {
      channels.push({
        key: 'email',
        href: buildMailtoUrl(lead, message),
        title: email,
        icon: IconMail,
      });
    }

    const phoneRaw = firstNonEmpty(lead.phone, lead.contactEnrichment?.mobiles?.[0]);
    const phoneKind = String(lead.phoneKind || lead.contactEnrichment?.phoneKind || '').toLowerCase();
    if (phoneRaw) {
      const canMessagePhone = phoneKind === 'mobile' || !phoneKind;
      if (canMessagePhone) {
        const waPhone = normaliseAuPhone(phoneRaw);
        const whatsappHref = buildWhatsappUrl(lead, message);
        const smsHref = buildSmsUrl(lead, message);
        if (whatsappHref) {
          channels.push({
            key: 'whatsapp',
            href: whatsappHref,
            title: waPhone ? `wa.me/${waPhone}` : phoneRaw,
            icon: IconBrandWhatsapp,
          });
        }
        if (smsHref) {
          channels.push({
            key: 'sms',
            href: smsHref,
            title: waPhone ? `sms:${waPhone}` : phoneRaw,
            icon: IconMessage,
          });
        }
      }
      if (phoneKind === 'landline') {
        const telPhone = normaliseAuPhone(phoneRaw) || phoneRaw;
        channels.push({
          key: 'phone',
          href: `tel:${telPhone}`,
          title: phoneRaw,
          icon: IconDeviceMobile,
        });
      }
    }

    const instagram = normalizeUrl(lead.contactEnrichment?.socials?.instagram, { hostPrefix: 'https://instagram.com' });
    if (instagram) {
      channels.push({
        key: 'instagram',
        href: instagram,
        title: lead.contactEnrichment?.socials?.instagram || instagram,
        icon: IconBrandInstagram,
      });
    }
    const facebook = normalizeUrl(lead.contactEnrichment?.socials?.facebook, { hostPrefix: 'https://facebook.com' });
    if (facebook) {
      channels.push({
        key: 'facebook',
        href: facebook,
        title: lead.contactEnrichment?.socials?.facebook || facebook,
        icon: IconBrandFacebook,
      });
    }
    const linkedin = normalizeUrl(firstNonEmpty(lead.linkedinUrl, lead.contactEnrichment?.socials?.linkedin), {
      hostPrefix: 'https://linkedin.com/in',
    });
    if (linkedin) {
      channels.push({
        key: 'linkedin',
        href: linkedin,
        title: firstNonEmpty(lead.linkedinUrl, lead.contactEnrichment?.socials?.linkedin, linkedin),
        icon: IconBrandLinkedin,
      });
    }
    const website = normalizeUrl(firstNonEmpty(lead.website, lead.contactEnrichment?.website));
    if (website) {
      channels.push({
        key: 'website',
        href: website,
        title: firstNonEmpty(lead.website, lead.contactEnrichment?.website, website),
        icon: IconWorld,
      });
    }

    return channels;
  }, [
    lead.email,
    lead.phone,
    lead.phoneKind,
    lead.linkedinUrl,
    lead.website,
    lead.contactEnrichment,
    lead.approvedMessage,
    lead.draftMessages,
  ]);

  const orderedMagicLinks = useMemo(() => {
    const items = Array.isArray(lead.magicLinks) ? [...lead.magicLinks] : [];
    // Dedupe unsent duplicates on the same destinationUrl — keep the most
    // recent entry per URL (see coach board for the historical-bug context).
    const byKey = new Map();
    for (const item of items) {
      if (item?.sentAt) {
        byKey.set(`sent:${item.linkId}`, item);
        continue;
      }
      const url = String(item?.destinationUrl || '').trim();
      const dedupeKey = url ? `unsent:${url}` : `unsent:${item?.linkId || Math.random()}`;
      const prior = byKey.get(dedupeKey);
      if (!prior) {
        byKey.set(dedupeKey, item);
        continue;
      }
      const priorTs = new Date(prior?.lastActivityAt || 0).getTime();
      const nextTs = new Date(item?.lastActivityAt || 0).getTime();
      if (nextTs > priorTs) byKey.set(dedupeKey, item);
    }
    return [...byKey.values()].sort((a, b) => {
      const ta = new Date(a?.lastActivityAt || a?.readAt || a?.clickedAt || a?.sentAt || 0).getTime();
      const tb = new Date(b?.lastActivityAt || b?.readAt || b?.clickedAt || b?.sentAt || 0).getTime();
      return tb - ta;
    });
  }, [lead.magicLinks]);
  const visibleMagicLinks = orderedMagicLinks.slice(0, 3);
  const hiddenMagicLinkCount = Math.max(0, orderedMagicLinks.length - visibleMagicLinks.length);

  const cardStyle = isTerminal
    ? { background: terminal.bg, color: terminal.fg, border: `1px solid ${terminal.bg}` }
    : { background: '#fff', color: 'var(--aviary-ink)', border: '1px solid var(--mantine-color-sage-3)' };

  return (
    <Paper
      ref={dndRef}
      p="sm"
      radius="md"
      draggable={nativeDraggable}
      onDragStart={(e) => {
        if (!nativeDraggable) return;
        e.dataTransfer.setData('text/plain', lead.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart?.(lead.id);
      }}
      onDragEnd={() => {
        if (!nativeDraggable) return;
        onDragEnd?.();
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        ...cardStyle,
        ...(dndStyle || {}),
        cursor: (dndIsDragging || isDragging) ? 'grabbing' : (prospectHandle ? 'pointer' : 'grab'),
        opacity: (dndIsDragging || isDragging) ? 0.4 : 1,
        transition: 'opacity 120ms ease, box-shadow 120ms ease',
      }}
    >
      <Stack gap={8}>
        <Group gap={8} wrap="nowrap" align="flex-start">
          {prospectHandle && (
            <Box style={{
              marginTop: 2,
              opacity: isHovering ? 1 : 0.5,
              transition: 'opacity 120ms ease',
              color: 'var(--aviary-muted)',
              flexShrink: 0,
            }}>
              {prospectHandle}
            </Box>
          )}
          <Avatar src={lead.photoUrl || null} size={30} radius="xl" color={isTerminal ? 'gray' : 'sage'}>
            {initialsFor(lead.name)}
          </Avatar>
          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={600} style={{
              color: isTerminal ? '#fff' : 'var(--aviary-ink)',
              lineHeight: 1.25,
            }} truncate>
              {lead.name || '(no name)'}
            </Text>
            <Text size="xs" style={{
              color: isTerminal ? 'rgba(255,255,255,0.75)' : 'var(--aviary-muted)',
              lineHeight: 1.25,
            }} truncate>
              {lead.title || '—'}
            </Text>
            {lead.company && String(lead.company).trim().toLowerCase() !== String(lead.name || '').trim().toLowerCase() && (
              <Text size="xs" style={{
                color: isTerminal ? 'rgba(255,255,255,0.65)' : 'var(--aviary-muted)',
                lineHeight: 1.25,
              }} truncate>
                {lead.company}
              </Text>
            )}
          </Stack>
          {!isTerminal && <FitChip score={typeof lead.fitScore === 'number' && lead.fitScore >= 10 ? lead.fitScore : null} />}
        </Group>

        {channelLinks.length > 0 && (
          <Group gap={6} wrap="wrap">
            {channelLinks.map((channel) => {
              const Icon = channel.icon;
              return (
                <UnstyledButton
                  key={channel.key}
                  component="a"
                  href={channel.href}
                  target="_blank"
                  rel="noreferrer"
                  title={channel.title}
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Icon size={14} style={{ color: iconColor }} />
                </UnstyledButton>
              );
            })}
          </Group>
        )}

        {lane === 'engaged' && (
          <EngagementDots counts={lead.engagement} />
        )}

        {visibleMagicLinks.length > 0 && (
          <Stack gap={4}>
            {visibleMagicLinks.map((item) => (
              <MagicLinkPill
                key={item.linkId}
                item={item}
              />
            ))}
            {hiddenMagicLinkCount > 0 && (
              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                +{hiddenMagicLinkCount} more
              </Text>
            )}
          </Stack>
        )}

        {isTerminal && (
          <Text size="xs" fw={500} style={{ color: 'rgba(255,255,255,0.9)' }}>
            {(terminal.emoji ? `${terminal.emoji} ` : '')}
            {terminal.label}
            {lead.lastActivityAt ? ` · ${formatAge(lead.lastActivityAt)} ago` : ''}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function SortableProspectCard({
  lead,
  onClick,
  onNativeDragStart,
  onNativeDragEnd,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(lead.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    boxShadow: isDragging ? '0 10px 24px rgba(0, 0, 0, 0.16)' : 'none',
  };

  const handle = (
    <Box
      {...attributes}
      {...listeners}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      aria-label="Reorder prospect"
    >
      <IconGripVertical size={14} />
    </Box>
  );

  return (
    <LeadCard
      lead={lead}
      lane="prospect"
      onClick={onClick}
      onDragStart={onNativeDragStart}
      onDragEnd={onNativeDragEnd}
      isDragging={false}
      nativeDraggable
      dndRef={setNodeRef}
      prospectHandle={handle}
      dndStyle={style}
      dndIsDragging={isDragging}
    />
  );
}

// ─── Lane caption ────────────────────────────────────────────────────────────
function LaneCaption({ mode, text, workBadge }) {
  const dotColor = mode === 'auto' ? 'var(--mantine-color-sage-6)' : 'var(--aviary-disabled)';
  return (
    <Group gap={8} align="center" wrap="nowrap" px={4}>
      <Box style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
      <Text size="xs" style={{ color: 'var(--aviary-muted)', flex: 1 }} truncate>
        {text}
      </Text>
      {workBadge && (
        <Text
          size="xs"
          fw={600}
          style={{
            padding: '1px 8px',
            borderRadius: 999,
            background: 'var(--mantine-color-yellow-1)',
            border: '1px solid var(--mantine-color-yellow-6)',
            color: 'var(--mantine-color-yellow-8)',
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {workBadge}
        </Text>
      )}
    </Group>
  );
}

// ─── Top-up card ─────────────────────────────────────────────────────────────
function ProspectTopUpCard({ campaigns, onNew, onRefresh }) {
  const activeCampaigns = (campaigns || []).filter((c) => {
    const s = String(c.status || '').toLowerCase();
    return s !== 'archived' && s !== 'completed' && s !== 'draft';
  });
  const MAX_LIST = 5;
  const visible = activeCampaigns.slice(0, MAX_LIST);
  const overflow = Math.max(0, activeCampaigns.length - visible.length);
  return (
    <Paper p="sm" radius="md" style={{ background: '#FFFCF3', border: '1px dashed #F6C265' }}>
      <Stack gap={6}>
        <Group gap={6} wrap="nowrap" align="center">
          <IconPlus size={13} style={{ color: '#8A5A00' }} />
          <Text size="xs" fw={600} style={{ color: '#8A5A00' }}>Add more prospects</Text>
        </Group>
        <Text size="xs" style={{ color: '#8A5A00', opacity: 0.85 }}>
          Fill this lane when it runs low.
        </Text>
        <UnstyledButton onClick={onNew} style={{ padding: '4px 2px', borderRadius: 6, textAlign: 'left' }}>
          <Text size="xs" fw={500} style={{ color: '#8A5A00' }}>+ New campaign wizard →</Text>
        </UnstyledButton>
        {visible.map((c) => (
          <UnstyledButton
            key={c.id}
            onClick={() => onRefresh(c)}
            style={{ padding: '4px 2px', borderRadius: 6, textAlign: 'left' }}
          >
            <Text size="xs" fw={500} style={{ color: '#8A5A00' }} truncate>
              ↻ Refresh &quot;{c.name || 'Untitled'}&quot; →
            </Text>
          </UnstyledButton>
        ))}
        {overflow > 0 && (
          <Text size="xs" style={{ color: '#8A5A00', opacity: 0.6, fontStyle: 'italic' }}>
            +{overflow} more in Campaigns
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

// ─── AddProspectTile — paste-a-LinkedIn-URL manual add ───────────────────────
function AddProspectTile({ selectedCampaignId, campaigns, onAdded }) {
  const api = useApi();
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const activeCampaigns = (campaigns || []).filter((c) => {
    const s = String(c.status || '').toLowerCase();
    return s !== 'archived' && s !== 'completed';
  });
  const filterCampaign = selectedCampaignId && selectedCampaignId !== 'all'
    ? activeCampaigns.find((c) => String(c.id) === String(selectedCampaignId))
    : null;
  const [pickedCampaignId, setPickedCampaignId] = useState(
    filterCampaign?.id || activeCampaigns[0]?.id || null
  );
  useEffect(() => {
    if (filterCampaign?.id) setPickedCampaignId(filterCampaign.id);
  }, [filterCampaign?.id]);

  const targetCampaign = filterCampaign
    || activeCampaigns.find((c) => String(c.id) === String(pickedCampaignId))
    || activeCampaigns[0];
  const disabled = !targetCampaign;

  async function submit(e) {
    e.preventDefault();
    if (disabled || submitting || !targetCampaign) return;
    const trimmed = url.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const { lead } = await addProspectByUrl(api, targetCampaign.id, trimmed);
      notifications.show({
        title: 'Prospect added',
        message: `${lead.name || 'New prospect'} is waiting in ${targetCampaign.name || 'the campaign'}.`,
        color: 'sage',
      });
      setUrl('');
      onAdded?.(lead);
    } catch (err) {
      notifications.show({
        title: 'Could not add prospect',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }

  const showPicker = !filterCampaign && activeCampaigns.length > 1;
  const hint = disabled
    ? 'create a campaign first'
    : (submitting
        ? 'looking them up…'
        : (filterCampaign ? `adds to ${filterCampaign.name || 'this campaign'}` : `adds to ${targetCampaign?.name || 'campaign'}`));

  return (
    <Paper p="sm" radius="md" style={{
      background: 'transparent',
      border: '1px dashed var(--aviary-disabled)',
      opacity: disabled ? 0.5 : 1,
    }}>
      <form onSubmit={submit}>
        <Stack gap={6}>
          <Group gap={6} align="center">
            <IconBrandLinkedin size={13} style={{ color: 'var(--aviary-muted)' }} />
            <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)' }}>Add one by LinkedIn URL</Text>
          </Group>
          <TextInput
            size="xs"
            placeholder="paste a LinkedIn URL…"
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit(e);
            }}
            disabled={disabled || submitting}
            rightSectionPointerEvents="all"
            rightSectionWidth={32}
            rightSection={(
              <ActionIcon
                type="submit"
                size="xs"
                color="sage"
                variant="filled"
                loading={submitting}
                disabled={disabled || submitting || !url.trim()}
                aria-label="Add prospect by LinkedIn URL"
              >
                <IconArrowRight size={12} />
              </ActionIcon>
            )}
          />
          {showPicker && (
            <Select
              size="xs"
              value={pickedCampaignId ? String(pickedCampaignId) : null}
              onChange={(v) => setPickedCampaignId(v)}
              data={activeCampaigns.map((c) => ({ value: String(c.id), label: c.name || 'Untitled campaign' }))}
              allowDeselect={false}
              withCheckIcon={false}
              placeholder="pick a campaign"
              styles={{ input: { fontSize: 12 } }}
            />
          )}
          <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>{hint}</Text>
        </Stack>
      </form>
    </Paper>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
const MAX_VISIBLE_PER_LANE = 8;
const DRAG_EDGE_PX = 60;
const DRAG_SCROLL_SPEED = 12;

export default function AviaryV2BoardBusinessPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [error, setError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [draggingLeadId, setDraggingLeadId] = useState(null);
  const [hoverLane, setHoverLane] = useState(null);
  const [expandedLanes, setExpandedLanes] = useState(() => new Set());
  const boardRef = useRef(null);
  const prospectDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const toggleLaneExpanded = useCallback((lane) => {
    setExpandedLanes((prev) => {
      const next = new Set(prev);
      if (next.has(lane)) next.delete(lane);
      else next.add(lane);
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const [leadData, campData] = await Promise.all([
        fetchLeads(api, { flow: 'business' }),
        fetchCampaigns(api, { flow: 'business' }).catch(() => []),
      ]);
      const leadList = Array.isArray(leadData) ? leadData : (leadData?.leads || []);
      const campList = Array.isArray(campData) ? campData : (campData?.campaigns || []);
      setLeads(leadList);
      setCampaigns(campList);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Refetch on tab focus / visibility change (same rationale as coach board).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [load]);

  useEffect(() => {
    if (!draggingLeadId) return undefined;

    function findScrollableAncestor(node) {
      let current = node;
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const overflowX = style.overflowX || style.overflow;
        const overflowY = style.overflowY || style.overflow;
        const canScrollX = /(auto|scroll)/.test(overflowX) && current.scrollWidth > current.clientWidth;
        const canScrollY = /(auto|scroll)/.test(overflowY) && current.scrollHeight > current.clientHeight;
        if (canScrollX || canScrollY) return current;
        current = current.parentElement;
      }
      return null;
    }

    const scrollTarget = findScrollableAncestor(boardRef.current);
    let dx = 0;
    let dy = 0;
    let rafId = null;

    function stopLoop() {
      dx = 0;
      dy = 0;
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    function step() {
      if (dx === 0 && dy === 0) {
        rafId = null;
        return;
      }
      if (scrollTarget) {
        if (dx !== 0) scrollTarget.scrollLeft += dx;
        if (dy !== 0) scrollTarget.scrollTop += dy;
      } else {
        window.scrollBy(dx, dy);
      }
      rafId = requestAnimationFrame(step);
    }

    function onDragOver(e) {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      dx = clientX < DRAG_EDGE_PX
        ? -DRAG_SCROLL_SPEED
        : (clientX > innerWidth - DRAG_EDGE_PX ? DRAG_SCROLL_SPEED : 0);
      dy = clientY < DRAG_EDGE_PX
        ? -DRAG_SCROLL_SPEED
        : (clientY > innerHeight - DRAG_EDGE_PX ? DRAG_SCROLL_SPEED : 0);
      if ((dx !== 0 || dy !== 0) && rafId == null) rafId = requestAnimationFrame(step);
      if (dx === 0 && dy === 0) stopLoop();
    }

    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragend', stopLoop);
    window.addEventListener('dragcancel', stopLoop);
    window.addEventListener('drop', stopLoop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragend', stopLoop);
      window.removeEventListener('dragcancel', stopLoop);
      window.removeEventListener('drop', stopLoop);
      stopLoop();
    };
  }, [draggingLeadId]);

  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    let active = leads.filter((l) => l.archived !== true);
    if (selectedCampaign !== 'all') {
      active = active.filter((l) => String(l.campaignId || '') === String(selectedCampaign));
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      active = active.filter((l) => {
        const name = String(l.name || '').toLowerCase();
        const company = String(l.company || '').toLowerCase();
        const title = String(l.title || '').toLowerCase();
        return name.includes(q) || company.includes(q) || title.includes(q);
      });
    }
    return active;
  }, [leads, selectedCampaign, searchQuery]);

  const byLane = useMemo(() => {
    const map = { prospect: [], engaged: [], completed: [] };
    for (const l of filteredLeads) {
      const lane = laneOf(l);
      if (map[lane]) map[lane].push(l);
    }
    // Prospect: sortOrder is canonical once backfilled. Null sortOrder falls back
    // to the legacy display order (manual URL-adds first, then fitScore DESC).
    map.prospect.sort((a, b) => {
      const aOrder = typeof a.sortOrder === 'number' && Number.isFinite(a.sortOrder)
        ? a.sortOrder
        : Number.POSITIVE_INFINITY;
      const bOrder = typeof b.sortOrder === 'number' && Number.isFinite(b.sortOrder)
        ? b.sortOrder
        : Number.POSITIVE_INFINITY;
      if (aOrder !== bOrder) return aOrder - bOrder;

      const aManual = typeof a.fitScore !== 'number';
      const bManual = typeof b.fitScore !== 'number';
      if (aManual !== bManual) return aManual ? -1 : 1;
      if (aManual && bManual) {
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      }
      return b.fitScore - a.fitScore;
    });
    for (const key of ['engaged', 'completed']) {
      map[key].sort((a, b) => {
        const at = new Date(a.lastTouchAt || a.updatedAt || a.createdAt || 0).getTime();
        const bt = new Date(b.lastTouchAt || b.updatedAt || b.createdAt || 0).getTime();
        return bt - at;
      });
    }
    return map;
  }, [filteredLeads]);

  const campaignSelectData = useMemo(() => ([
    { value: 'all', label: 'All campaigns' },
    ...campaigns.map((c) => ({ value: String(c.id), label: c.name || 'Untitled' })),
  ]), [campaigns]);

  const selectedCampaignObj = campaigns.find((c) => String(c.id) === String(selectedCampaign)) || null;

  const laneCounts = {
    prospect: byLane.prospect.length,
    engaged: byLane.engaged.length,
    completed: byLane.completed.length,
  };

  const handleProspectSortEnd = useCallback(async (event) => {
    setDraggingLeadId(null);
    setHoverLane(null);
    const activeId = String(event?.active?.id || '');
    const overId = String(event?.over?.id || '');
    if (!activeId || !overId || activeId === overId) return;

    const visibleProspects = [...(byLane.prospect || [])];
    const oldIndex = visibleProspects.findIndex((lead) => String(lead.id) === activeId);
    const newIndex = visibleProspects.findIndex((lead) => String(lead.id) === overId);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

    const reordered = arrayMove(visibleProspects, oldIndex, newIndex);
    const movedLead = reordered.find((lead) => String(lead.id) === activeId);
    if (!movedLead) return;

    const nextSortOrder = computeProspectDropSortOrder(reordered, activeId, movedLead.sortOrder);
    if (!Number.isFinite(nextSortOrder) || nextSortOrder === movedLead.sortOrder) return;

    const previousSnapshot = leads;
    setLeads((prev) => (prev || []).map((lead) => (
      String(lead.id) === activeId
        ? { ...lead, sortOrder: nextSortOrder, updatedAt: new Date().toISOString() }
        : lead
    )));

    try {
      await updateLead(api, activeId, { sortOrder: nextSortOrder });
    } catch (err) {
      setLeads(previousSnapshot);
      notifications.show({
        title: 'Could not save new order',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    }
  }, [api, byLane.prospect, leads]);

  async function handleDrop(leadId, targetLane) {
    const lead = (leads || []).find((l) => String(l.id) === String(leadId));
    if (!lead) return;
    const currentLane = laneOf(lead);
    if (currentLane === targetLane) return;
    const patch = laneToPatch(targetLane);
    if (!patch) return;

    const prevSnapshot = leads;
    setLeads((prev) => (prev || []).map((l) => (
      String(l.id) === String(leadId) ? { ...l, ...patch } : l
    )));

    try {
      await updateLead(api, leadId, patch);
      notifications.show({
        title: `Moved to ${LANE_TITLES[targetLane]}`,
        message: `${lead.name || 'Prospect'} · stage updated`,
        color: 'sage',
      });
    } catch (err) {
      setLeads(prevSnapshot);
      notifications.show({
        title: 'Could not move card',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    }
  }

  function handleProspectAdded(lead) {
    setLeads((prev) => (prev ? [lead, ...prev] : [lead]));
    load();
  }

  const openNewCampaign = useCallback(() => {
    navigate('/v2?new=1');
  }, [navigate]);

  const openRefreshCampaign = useCallback((campaign) => {
    const target = campaign || selectedCampaignObj;
    if (!target) return;
    navigate(`/v2?mode=edit&campaignId=${encodeURIComponent(target.id)}`);
  }, [navigate, selectedCampaignObj]);

  const openLead = useCallback((lead) => {
    const leadId = lead?.id;
    if (!leadId) return;
    navigate(`/v2/leads/${leadId}`, { state: { back: { path: BOARD_PATH, label: 'Board' } } });
  }, [navigate]);

  const openLaneList = useCallback((lane) => {
    if (lane === 'prospect') {
      const first = byLane.prospect[0];
      if (first) {
        navigate(`/v2/leads/${first.id}?tab=message`, { state: { back: { path: BOARD_PATH, label: 'Board' } } });
        return;
      }
    }
    navigate('/v2/campaigns');
  }, [byLane.prospect, navigate]);

  // Business lanes have no automation yet — quiet manual captions with a work
  // badge when a lane has cards waiting.
  const captionFor = (lane) => {
    if (lane === 'prospect') {
      return {
        mode: 'manual',
        text: 'lane waits for you',
        workBadge: laneCounts.prospect > 0 ? `${laneCounts.prospect} to contact` : null,
      };
    }
    if (lane === 'engaged') {
      return {
        mode: 'manual',
        text: 'warm — book the meeting',
        workBadge: laneCounts.engaged > 0 ? `${laneCounts.engaged} to book` : null,
      };
    }
    if (lane === 'completed') {
      return {
        mode: 'auto',
        text: 'closed outcomes',
        workBadge: null,
      };
    }
    return null;
  };

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn&apos;t load the pipeline</Text>
          <Text size="sm" c="dimmed">{error}</Text>
        </Stack>
      </Center>
    );
  }
  if (leads === null) return <Center py={80}><Loader color="sage" /></Center>;

  return (
    <Box ref={boardRef} style={{ padding: '24px 20px 120px' }}>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Group gap="md" align="center">
            <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>Businesses</Text>
          </Group>
          <Group gap="sm">
            <TextInput
              placeholder="Search name, company, title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
              styles={{ input: { width: 240 } }}
            />
            <Select
              value={selectedCampaign}
              onChange={(v) => setSelectedCampaign(v || 'all')}
              data={campaignSelectData}
              allowDeselect={false}
              withCheckIcon={false}
              styles={{ input: { width: 220 } }}
            />
          </Group>
        </Group>

        <Group align="stretch" wrap="nowrap" gap="md" style={{ paddingBottom: 8 }}>
          {LANES.map((lane) => {
            const laneLeads = byLane[lane];
            const isExpanded = expandedLanes.has(lane);
            const visible = isExpanded ? laneLeads : laneLeads.slice(0, MAX_VISIBLE_PER_LANE);
            const hidden = Math.max(0, laneLeads.length - visible.length);
            const caption = captionFor(lane);

            return (
              <Stack
                key={lane}
                gap="xs"
                onDragOver={(e) => {
                  if (!draggingLeadId) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (hoverLane !== lane) setHoverLane(lane);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setHoverLane((prev) => (prev === lane ? null : prev));
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const leadId = e.dataTransfer.getData('text/plain');
                  setHoverLane(null);
                  if (leadId) handleDrop(leadId, lane);
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: 10,
                  borderRadius: 12,
                  background: hoverLane === lane ? 'var(--mantine-color-sage-0)' : 'transparent',
                  outline: hoverLane === lane ? '2px dashed var(--mantine-color-sage-4)' : 'none',
                  outlineOffset: hoverLane === lane ? -4 : 0,
                  transition: 'background 120ms ease',
                }}
              >
                <Group justify="space-between" align="center" wrap="nowrap" style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  padding: '6px 10px',
                  borderRadius: 8,
                  background: '#fff',
                  border: '1px solid var(--aviary-disabled)',
                }}>
                  <UnstyledButton
                    onClick={() => openLaneList(lane)}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Group gap={6} align="baseline">
                        <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>{LANE_TITLES[lane]}</Text>
                        <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{laneCounts[lane]}</Text>
                      </Group>
                      <IconArrowUpRight size={12} style={{ color: 'var(--aviary-muted)' }} />
                    </Group>
                  </UnstyledButton>
                </Group>

                {caption && (
                  <LaneCaption
                    mode={caption.mode}
                    text={caption.text}
                    workBadge={caption.workBadge}
                  />
                )}

                {lane === 'prospect' && (
                  <ProspectTopUpCard
                    campaigns={campaigns}
                    onNew={openNewCampaign}
                    onRefresh={openRefreshCampaign}
                  />
                )}

                <Stack gap="xs">
                  {lane === 'prospect' ? (
                    <DndContext
                      sensors={prospectDndSensors}
                      collisionDetection={closestCorners}
                      onDragStart={({ active }) => setDraggingLeadId(String(active?.id || ''))}
                      onDragCancel={() => {
                        setDraggingLeadId(null);
                        setHoverLane(null);
                      }}
                      onDragEnd={handleProspectSortEnd}
                    >
                      <SortableContext
                        items={visible.map((lead) => String(lead.id))}
                        strategy={verticalListSortingStrategy}
                      >
                        <Stack gap="xs">
                          {visible.map((lead) => (
                            <SortableProspectCard
                              key={lead.id}
                              lead={lead}
                              onClick={() => openLead(lead)}
                              onNativeDragStart={setDraggingLeadId}
                              onNativeDragEnd={() => { setDraggingLeadId(null); setHoverLane(null); }}
                            />
                          ))}
                        </Stack>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    visible.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        lane={lane}
                        isDragging={draggingLeadId === lead.id}
                        onDragStart={setDraggingLeadId}
                        onDragEnd={() => { setDraggingLeadId(null); setHoverLane(null); }}
                        onClick={() => openLead(lead)}
                      />
                    ))
                  )}
                  {laneLeads.length === 0 && (
                    <Box style={{
                      padding: 20, borderRadius: 10,
                      border: '1px dashed var(--aviary-disabled)', textAlign: 'center',
                    }}>
                      <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>empty</Text>
                    </Box>
                  )}
                  {hidden > 0 && (
                    <UnstyledButton
                      onClick={() => toggleLaneExpanded(lane)}
                      style={{
                        display: 'block', padding: 12, borderRadius: 10,
                        border: '1px dashed var(--aviary-disabled)', textAlign: 'center',
                      }}
                    >
                      <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
                        +{hidden} more
                      </Text>
                    </UnstyledButton>
                  )}
                  {isExpanded && laneLeads.length > MAX_VISIBLE_PER_LANE && (
                    <UnstyledButton
                      onClick={() => toggleLaneExpanded(lane)}
                      style={{
                        display: 'block', padding: 8, borderRadius: 10, textAlign: 'center',
                      }}
                    >
                      <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
                        ↑ show less
                      </Text>
                    </UnstyledButton>
                  )}

                  {lane === 'prospect' && (
                    <AddProspectTile
                      selectedCampaignId={selectedCampaign}
                      campaigns={campaigns}
                      onAdded={handleProspectAdded}
                    />
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Group>
      </Stack>
    </Box>
  );
}
